import { buildCustomizedURL, getAssetImageUrl, isYes } from '@util/common.js';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import NumberInput from '@components/input/number.input.jsx';
import YesNoInput from '@components/input/yes-no.input.jsx';
import CharitySection from '@components/shared/charity-section.jsx';
import LinkSetup from '@components/overlays/shared/link-setup.jsx';
import { BallPitOverlay } from '@playlive/overlay-core/overlays/ball-pit';
import { useContext, useEffect, useState } from 'preact/hooks';
import { ProductContext } from '../../../context/productContext.js';
import useTiltifyConfig from '@hooks/useTiltifyConfig.js';
import useTwitchConfig from '@hooks/useTwitchConfig.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import NumberSliderInput from '@components/input/number-slider.input.jsx';
import DevCustomizeOptions from '@components/shared/dev-customize-options.jsx';
import boomGif from '@assets/overlays/ball-pit/boom.gif';
import Picker from '@components/input/picker.input.jsx';
import useArtSpaceOverlayConfig from './art-space-overlay.config.js';

export default function ArtSpaceOverlayPage() {
  const { config, setConfigState, resetToDefault } = useArtSpaceOverlayConfig();
  const overlayInfo = useContext(ProductContext);
  const tiltifyConfig = useTiltifyConfig();
  const twitchConfig = useTwitchConfig();
  const [rate, setRate] = useState(10);
  const overlayURL = buildCustomizedURL({
    config: {
      ...config,
      ...twitchConfig,
      ...tiltifyConfig,
    },
    urlBase: overlayInfo.type,
    addCharityType: true,
  });

  const [demoDonations, setDemoDonations] = useState([
    {
      id: 'five',
      donor_name: 'Andy Smith',
      completed_at: '',
      amount: {
        value: '5',
      },
    },
    {
      id: 'ten',
      donor_name: 'John Smith',
      completed_at: '',
      amount: {
        value: '10',
      },
    },
    {
      id: 'hundred',
      donor_name: 'Anonymous',
      completed_at: '',
      amount: {
        value: '100',
      },
    },
    {
      id: 'thousand',
      donor_name: 'Jay L.',
      completed_at: '',
      amount: {
        value: '1000',
      },
    },
    {
      id: 'ten-thousand',
      donor_name: 'EpicGamer72',
      completed_at: '',
      amount: {
        value: '10000',
      },
    },
  ]);

  useEffect(() => {
    let timeout;
    const generateRandom = (max, min) => {
      return Math.random() * (max - min) + min;
    };

    const generateRandomDonation = () => {
      const amountValue = `${generateRandom(1000, 10)}`;

      return {
        id: window.crypto.randomUUID(),
        donor_name: `Anonymous ${parseInt(amountValue)}`,
        completed_at: '',
        amount: {
          value: amountValue,
        },
      };
    };

    function generateTimeout() {
      timeout = setTimeout(() => {
        setDemoDonations(() => [generateRandomDonation()]);
        generateTimeout();
      }, 1000 * rate);
    }

    generateTimeout();

    return () => {
      clearTimeout(timeout);
    };
  }, [rate]);

  const setStartingBallSize = (event) => {
    const ballSize = event.target.value;

    if (ballSize >= Number(config.maxScale)) {
      setConfigState({ target: { name: 'maxScale', value: ballSize + 1 } });
    }
    event.target.value = ballSize;
    setConfigState(event);
  };

  const setMaxScale = (event) => {
    const ballSize = event.target.value;

    if (ballSize <= Number(config.startSize)) {
      setConfigState({ target: { name: 'startSize', value: ballSize - 1 } });
    }

    setConfigState(event);
  };

  const setMaxDonationAmount = (event) => {
    const maxDonationAmount = Number(event.target.value);

    if (maxDonationAmount <= Number(config.minAmount)) {
      setConfigState({
        target: { name: 'minAmount', value: maxDonationAmount - 1 },
      });
    }

    setConfigState(event);
  };

  const setMinDonationAmount = (event) => {
    const minDonationAmount = Number(event.target.value);

    if (minDonationAmount >= Number(config.maxAmount)) {
      setConfigState({
        target: { name: 'maxAmount', value: minDonationAmount + 1 },
      });
    }

    setConfigState(event);
  };

  const typeOptions = [
    {
      name: 'Pizza',
      value: 'pizza',
    },
    {
      name: 'Tie Dye',
      value: 'tie-dye',
    },
    {
      name: 'Football',
      value: 'football',
    },
    {
      name: 'Heart',
      value: 'heart',
    },
    {
      name: 'UFO',
      value: 'ufo',
    },
    {
      name: 'Spaceship',
      value: 'spaceship',
    },
    {
      name: 'Green Butterfly',
      value: 'green-butterfly',
    },
  ];

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper
            allowScaling={false}
            addPadding={false}
            defaultScale={'scale-100'}
          >
            <BallPitOverlay
              type={config.ballType}
              startSize={config.startSize / 2}
              showNames={false}
              donations={demoDonations}
              maxBalls={config.maxBalls}
              mass={config.mass}
              density={config.density}
              friction={config.friction}
              frictionStatic={config.frictionStatic}
              frictionAir={config.frictionAir}
              maxAmount={config.maxAmount}
              maxScale={config.maxScale / 20}
              minAmount={config.minAmount}
              scaleSize={isYes(config.scaleSize)}
              previewScaleSize={0.9}
              clampVelocity={config.clampVelocity}
              clampAngularSpeed={config.clampAngularSpeed}
              explosionGifURL={getAssetImageUrl(boomGif)}
              explosionForceMagnitude={Number(config.explosionForceMagnitude)}
             randomColors={false} />
          </ScaledOverlayWrapper>
        </div>

        <div className="w-full mb-4 px-2">
          <CustomizeSection title="Appearance">
            <div className="mb-5">
              <Picker
                items={typeOptions}
                fieldName="ballType"
                onChange={setConfigState}
                currentValue={config.ballType}
                labelText="Ball Type"
              />
            </div>
          </CustomizeSection>
          <CustomizeSection title="Sizing">
            <YesNoInput
              mode="checkbox"
              onChange={setConfigState}
              currentValue={config.scaleSize}
              name="scaleSize"
              label="Scale the size of the balls relative to the donation amount"
            />

            <div
              tabIndex="0"
              className={`collapse ${isYes(config.scaleSize) ? 'collapse-open' : 'collapse-close'}`}
            >
              <div className="collapse-content px-0 user-select-none">
                <div className="my-6">
                  <NumberSliderInput
                    disabled={!isYes(config.scaleSize)}
                    fieldName="startSize"
                    value={Number(config.startSize)}
                    onChange={setStartingBallSize}
                    minValue={1}
                    step={1}
                    maxValue={10}
                    labelText="The starting size of the ball"
                    showValueSuffix={false}
                  />
                </div>

                <div className="mb-8">
                  <NumberSliderInput
                    disabled={!isYes(config.scaleSize)}
                    fieldName="maxScale"
                    value={Number(config.maxScale)}
                    onChange={setMaxScale}
                    minValue={Number(config.startSize) + 1}
                    step={1}
                    maxValue={Number(config.startSize) + 20}
                    labelText="The maximum size of the ball"
                    showValueSuffix={false}
                  />
                </div>

                <div className="mb-6">
                  <NumberInput
                    name="maxAmount"
                    value={Number(config.maxAmount)}
                    placeholder="100"
                    onChange={setMaxDonationAmount}
                    isCurrency={true}
                    minValue={100}
                    maxValue={1000}
                    labelText="Average large donation amount"
                  />
                </div>

                <NumberInput
                  name="minAmount"
                  value={Number(config.minAmount)}
                  placeholder="5"
                  onChange={setMinDonationAmount}
                  isCurrency={true}
                  minValue={1}
                  maxValue={100}
                  labelText="Minimum donation amount to create a new ball"
                />
              </div>
            </div>
          </CustomizeSection>

          <CustomizeSection title="Misc.">


            <div className="flex justify-between">
              <div className="mt-1 flex-grow">
                <button
                  onClick={resetToDefault}
                  className="btn btn-secondary mt-2 w-full"
                >
                  Reset to default
                </button>
              </div>
            </div>
          </CustomizeSection>

          <DevCustomizeOptions setConfigState={setConfigState} config={config}>
            <div className="my-6">
              <NumberSliderInput
                fieldName="rate"
                value={rate}
                onChange={(e) => setRate(parseFloat(e.target.value))}
                minValue={1}
                step={1}
                maxValue={10}
                labelText="How many seconds to wait before adding a ball (demo)"
                showValueSuffix={true}
              />
            </div>
            <div className="mb-6">
              <NumberInput
                name="maxBalls"
                value={Number(config.maxBalls)}
                placeholder="25"
                onChange={setConfigState}
                isCurrency={false}
                maxLength={4}
                labelText="The maximum amount of balls"
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="mass"
                value={Number(config.mass)}
                onChange={setConfigState}
                minValue={1}
                step={10}
                maxValue={10000}
                labelText="The ball's mass"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="density"
                value={Number(config.density)}
                onChange={setConfigState}
                minValue={1}
                step={10}
                maxValue={10000}
                labelText="The ball's density"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="friction"
                value={Number(config.friction)}
                onChange={setConfigState}
                minValue={0.0}
                step={0.0001}
                maxValue={1}
                labelText="The ball's friction"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="frictionAir"
                value={Number(config.frictionAir)}
                onChange={setConfigState}
                minValue={0.0}
                step={0.0001}
                maxValue={1}
                labelText="The ball's friction in the air"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="frictionStatic"
                value={Number(config.frictionStatic)}
                onChange={setConfigState}
                minValue={0.0}
                step={0.0001}
                maxValue={1}
                labelText="The ball's friction when static"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="restitution"
                value={Number(config.restitution)}
                onChange={setConfigState}
                minValue={0.0}
                step={0.1}
                maxValue={1}
                labelText="The ball's restitution"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="clampVelocity"
                value={Number(config.clampVelocity)}
                onChange={setConfigState}
                minValue={0.0}
                step={0.5}
                maxValue={100}
                labelText="The ball's max velocity"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="clampAngularSpeed"
                value={Number(config.clampAngularSpeed)}
                onChange={setConfigState}
                minValue={0.0}
                step={0.01}
                maxValue={1}
                labelText="The ball's max angular speed"
                showValueSuffix={true}
              />
            </div>
            <div className="my-6">
              <NumberSliderInput
                fieldName="explosionForceMagnitude"
                value={Number(config.explosionForceMagnitude)}
                onChange={setConfigState}
                minValue={0.001}
                step={0.001}
                maxValue={0.3}
                labelText="The ball's explosion force magnitude"
                showValueSuffix={true}
              />
            </div>
          </DevCustomizeOptions>

          <CharitySection
            onChange={setConfigState}
            charityType={config.charityType}
          />

          <LinkSetup
            charityType={config.charityType}
            obsDimensions={overlayInfo.obsDimensions}
            overlayName={overlayInfo.name}
            overlayURL={overlayURL}
          />
        </div>
      </div>
    </div>
  );
}
